/**
 * Scheduled Transactions Service
 *
 * CRUD operations for scheduled transactions.
 * These are recurring bills/income that the system auto-detects
 * or the user creates manually.
 */

import { db, ScheduledTransaction, SkippedOccurrence, SyncStatus } from './db';
import { generateId } from '@utils/id';
import { dataEvents } from './dataEvents';
import { syncManager } from './syncManager'; // BUG-034 FIX: Need to trigger sync on mutations

// ============================================================
// Types
// ============================================================

export type ScheduledTransactionInput = Omit<
  ScheduledTransaction,
  'id' | 'createdAt' | 'updatedAt' | 'syncStatus' | 'deletedAt'
>;

export type ScheduledTransactionUpdate = Partial<
  Omit<ScheduledTransaction, 'id' | 'createdAt'>
>;

// ============================================================
// Debug Logging
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[SCHEDULED]', ...args);
  }
}

// ============================================================
// Date Helpers
// ============================================================

/**
 * Get start of day for a date (midnight local time).
 */
function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Add months to a date.
 */
function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

/**
 * Add weeks to a date.
 */
function addWeeks(date: Date, weeks: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

/**
 * Add years to a date.
 */
function addYears(date: Date, years: number): Date {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

/**
 * Calculate next due date based on frequency.
 */
export function calculateNextDueDate(schedule: ScheduledTransaction): Date {
  const current = schedule.nextDueDate;

  switch (schedule.frequency) {
    case 'weekly':
      return addWeeks(current, 1);
    case 'biweekly':
      return addWeeks(current, 2);
    case 'monthly':
      return addMonths(current, 1);
    case 'yearly':
      return addYears(current, 1);
    default:
      return addMonths(current, 1);
  }
}

/**
 * Get all due dates between two dates for a schedule.
 */
export function getDueDatesBetween(
  startDate: Date,
  endDate: Date,
  schedule: ScheduledTransaction
): Date[] {
  const dates: Date[] = [];
  let current = new Date(startDate);

  // Ensure we start from the schedule's pattern
  if (schedule.dayOfMonth && schedule.frequency === 'monthly') {
    current.setDate(schedule.dayOfMonth);
    if (current < startDate) {
      current = addMonths(current, 1);
    }
  }

  const maxIterations = 100; // Safety limit
  let i = 0;

  while (current <= endDate && i < maxIterations) {
    if (current >= startDate) {
      dates.push(new Date(current));
    }

    // Advance to next occurrence
    switch (schedule.frequency) {
      case 'weekly':
        current = addWeeks(current, 1);
        break;
      case 'biweekly':
        current = addWeeks(current, 2);
        break;
      case 'monthly':
        current = addMonths(current, 1);
        break;
      case 'yearly':
        current = addYears(current, 1);
        break;
    }
    i++;
  }

  return dates;
}

// ============================================================
// CRUD Operations
// ============================================================

export const scheduledTransactionService = {
  /**
   * Create a new scheduled transaction.
   *
   * RECURRING-FIX: Issue 4A - Checks for existing schedule with same detectionKey
   * to prevent duplicates. If one exists (and is not deleted), returns existing ID.
   */
  async create(data: ScheduledTransactionInput): Promise<string> {
    try {
      if (!db.scheduledTransactions) {
        console.warn('[SCHED] scheduledTransactions table not available');
        return '';
      }

      // RECURRING-FIX: Issue 4A - Prevent duplicate schedules by detectionKey
      if (data.detectionKey) {
        const existing = await this.findByDetectionKey(data.detectionKey);
        if (existing && !existing.deletedAt) {
          log('[DEDUPE] Schedule already exists for:', data.detectionKey, '- returning existing ID');
          return existing.id;
        }
        // If existing is deleted (user dismissed), allow creating new one
        // This enables re-enabling recurring after stopping it
      }

      const id = generateId();
      const now = new Date();

      const schedule: ScheduledTransaction = {
        ...data,
        id,
        createdAt: now,
        updatedAt: now,
        syncStatus: 'pending',
      };

      await db.scheduledTransactions.add(schedule);
      log('[OK] Created scheduled transaction:', id, data.description);

      dataEvents.emit('all');
      syncManager.triggerPush(); // BUG-034 FIX: Sync to other devices
      return id;
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found - cannot create');
        return '';
      }
      throw err;
    }
  },

  /**
   * Get a scheduled transaction by ID.
   */
  async getById(id: string): Promise<ScheduledTransaction | undefined> {
    try {
      if (!db.scheduledTransactions) {
        return undefined;
      }
      return db.scheduledTransactions.get(id);
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return undefined;
      }
      throw err;
    }
  },

  /**
   * Get all scheduled transactions (excludes soft-deleted).
   * Returns empty array if table doesn't exist (schema not yet upgraded).
   */
  async getAll(): Promise<ScheduledTransaction[]> {
    try {
      // Check if table exists before querying
      if (!db.scheduledTransactions) {
        console.warn('[SCHED] scheduledTransactions table not available');
        return [];
      }
      const all = await db.scheduledTransactions.toArray();
      return all.filter((s) => !s.deletedAt);
    } catch (err) {
      // Handle NotFoundError when table doesn't exist yet
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found - returning empty');
        return [];
      }
      throw err;
    }
  },

  /**
   * Get active scheduled transactions (not paused, not ignored, not deleted).
   */
  async getActive(): Promise<ScheduledTransaction[]> {
    const all = await this.getAll();
    const now = new Date();

    return all.filter((s) => {
      // Skip permanently ignored
      if (s.isIgnoredPermanently) return false;

      // Skip if currently paused
      if (s.isPaused) {
        // Check if pause period has ended
        if (s.pausedUntil && s.pausedUntil <= now) {
          // Pause period ended - should be active
          // Note: We'll update the record separately
          return true;
        }
        return false;
      }

      // Skip if past end date
      if (s.endDate && s.endDate < now) return false;

      return true;
    });
  },

  /**
   * Get scheduled transactions by account.
   */
  async getByAccount(accountId: string): Promise<ScheduledTransaction[]> {
    const all = await this.getAll();
    return all.filter((s) => s.accountId === accountId);
  },

  /**
   * Find a scheduled transaction by detection key (normalized payee).
   * BUG-037 FIX: Return deleted records too so bridge can see dismissed patterns
   * and avoid recreating them. Prefer non-deleted if both exist.
   */
  async findByDetectionKey(key: string): Promise<ScheduledTransaction | undefined> {
    try {
      if (!db.scheduledTransactions) {
        return undefined;
      }
      const normalizedKey = key.toLowerCase().trim();
      // Get ALL records with this key, INCLUDING deleted ones
      // Bridge needs to see deleted records to know not to recreate
      const records = await db.scheduledTransactions
        .where('detectionKey')
        .equals(normalizedKey)
        .toArray();
      if (records.length === 0) return undefined;
      // Prefer non-deleted, but return deleted if that's all we have
      const active = records.find((r) => !r.deletedAt);
      return active || records[0];
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return undefined;
      }
      throw err;
    }
  },

  /**
   * Get overdue scheduled transactions.
   * Returns schedules that are 2+ days past their next due date.
   */
  async getOverdue(): Promise<ScheduledTransaction[]> {
    const active = await this.getActive();
    const today = startOfDay(new Date());
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    return active.filter((s) => {
      const dueDate = startOfDay(s.nextDueDate);
      return dueDate <= twoDaysAgo;
    });
  },

  /**
   * Update a scheduled transaction.
   */
  async update(id: string, data: ScheduledTransactionUpdate): Promise<void> {
    try {
      if (!db.scheduledTransactions) {
        console.warn('[SCHED] scheduledTransactions table not available');
        return;
      }
      await db.scheduledTransactions.update(id, {
        ...data,
        updatedAt: new Date(),
        syncStatus: 'pending' as SyncStatus,
      });
      log('[OK] Updated scheduled transaction:', id);

      dataEvents.emit('all');
      syncManager.triggerPush(); // BUG-034 FIX: Sync to other devices
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return;
      }
      throw err;
    }
  },

  /**
   * Pause a scheduled transaction.
   * Can optionally specify pausedUntil date for temporary pause.
   */
  async pause(id: string, pausedUntil?: Date): Promise<void> {
    await this.update(id, {
      isPaused: true,
      pausedUntil,
    });
    log('[OK] Paused scheduled transaction:', id, pausedUntil ? `until ${pausedUntil}` : 'indefinitely');
  },

  /**
   * Resume a paused scheduled transaction.
   */
  async resume(id: string): Promise<void> {
    await this.update(id, {
      isPaused: false,
      pausedUntil: undefined,
    });
    log('[OK] Resumed scheduled transaction:', id);
  },

  /**
   * Auto-resume all paused schedules whose pausedUntil has passed.
   * Call this at app startup and periodically.
   * Returns the number of schedules resumed.
   */
  async autoResumeExpiredPauses(): Promise<number> {
    try {
      if (!db.scheduledTransactions) {
        return 0;
      }

      const now = new Date();
      const all = await db.scheduledTransactions.toArray();

      // Find paused schedules with expired pausedUntil
      const expired = all.filter((s) =>
        s.isPaused &&
        s.pausedUntil &&
        s.pausedUntil <= now &&
        !s.deletedAt &&
        !s.isIgnoredPermanently
      );

      if (expired.length === 0) {
        return 0;
      }

      // Resume each expired schedule
      for (const schedule of expired) {
        // BUG-064 FIX: Guard against undefined id
        if (!schedule?.id) {
          console.warn('[SCHED] autoResumeExpiredPauses: schedule missing id', schedule);
          continue;
        }
        await this.update(schedule.id, {
          isPaused: false,
          pausedUntil: undefined,
        });
        log('[OK] Auto-resumed expired pause:', schedule.description);
      }

      log('[OK] Auto-resumed', expired.length, 'paused schedules');
      return expired.length;
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return 0;
      }
      throw err;
    }
  },

  /**
   * Ignore a scheduled transaction permanently.
   *
   * RECURRING-FIX: Issue 2 - Uses forceImmediatePush() to ensure ignore status
   * reaches server before user switches devices.
   */
  async ignorePermanently(id: string): Promise<void> {
    try {
      if (!db.scheduledTransactions) {
        console.warn('[SCHED] scheduledTransactions table not available');
        return;
      }
      await db.scheduledTransactions.update(id, {
        isIgnoredPermanently: true,
        isPaused: true,
        updatedAt: new Date(),
        syncStatus: 'pending' as SyncStatus,
      });
      log('[OK] Permanently ignored scheduled transaction:', id);

      dataEvents.emit('all');
      // RECURRING-FIX: Force immediate sync for critical ignore action
      syncManager.forceImmediatePush();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return;
      }
      throw err;
    }
  },

  /**
   * Increment dismiss count and handle escalation.
   * Returns true if this was the 2nd+ dismiss (should show popup).
   */
  async dismiss(id: string): Promise<{ showEscalationPopup: boolean }> {
    const schedule = await this.getById(id);
    if (!schedule) {
      return { showEscalationPopup: false };
    }

    const newCount = (schedule.dismissCount || 0) + 1;
    await this.update(id, {
      dismissCount: newCount,
      isPaused: true,
    });

    log('[OK] Dismissed scheduled transaction:', id, 'count:', newCount);

    // Show escalation popup on 2nd dismiss
    return { showEscalationPopup: newCount >= 2 };
  },

  /**
   * Delete a scheduled transaction (soft delete for sync).
   *
   * RECURRING-FIX: Issue 2 - Uses forceImmediatePush() to ensure deletion
   * reaches server before user switches devices.
   */
  async delete(id: string): Promise<void> {
    try {
      if (!db.scheduledTransactions) {
        console.warn('[SCHED] scheduledTransactions table not available');
        return;
      }
      await db.scheduledTransactions.update(id, {
        deletedAt: new Date(),
        syncStatus: 'pending',
        updatedAt: new Date(),
      });
      log('[OK] Soft deleted scheduled transaction:', id);

      dataEvents.emit('all');
      // RECURRING-FIX: Force immediate sync for critical deletion action
      syncManager.forceImmediatePush();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return;
      }
      throw err;
    }
  },

  /**
   * Advance next due date after creating a transaction.
   * IMPORTANT: Loops until nextDueDate is in the FUTURE.
   * This prevents old items from reappearing after skip/pay.
   */
  async advanceToNextDueDate(id: string): Promise<void> {
    const schedule = await this.getById(id);
    if (!schedule) return;

    const today = startOfDay(new Date());
    const originalDate = schedule.nextDueDate;
    let nextDate = schedule.nextDueDate;

    // Keep advancing until we reach a future date
    // Safety limit of 24 iterations (2 years for monthly)
    const maxIterations = 24;
    let iterations = 0;

    while (startOfDay(nextDate) <= today && iterations < maxIterations) {
      // Create a temp schedule object with the current nextDueDate
      const tempSchedule = { ...schedule, nextDueDate: nextDate };
      nextDate = calculateNextDueDate(tempSchedule);
      iterations++;
    }

    await this.update(id, {
      nextDueDate: nextDate,
      lastGeneratedDate: originalDate,
    });

    if (iterations > 1) {
      log('[OK] Advanced due date:', schedule.description, '->', nextDate, `(skipped ${iterations} past occurrences)`);
    } else {
      log('[OK] Advanced due date:', schedule.description, '->', nextDate);
    }
  },

  /**
   * Get count of scheduled transactions.
   */
  async count(): Promise<number> {
    const all = await this.getAll();
    return all.length;
  },

  /**
   * Get pending sync records.
   */
  async getPendingSync(): Promise<ScheduledTransaction[]> {
    try {
      if (!db.scheduledTransactions) {
        return [];
      }
      return db.scheduledTransactions.where('syncStatus').equals('pending').toArray();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return [];
      }
      throw err;
    }
  },

  /**
   * Mark records as synced.
   */
  async markSynced(ids: string[]): Promise<void> {
    try {
      if (!db.scheduledTransactions) {
        return;
      }
      await db.scheduledTransactions
        .where('id')
        .anyOf(ids)
        .modify({ syncStatus: 'synced' });
      log('[OK] Marked', ids.length, 'scheduled transactions as synced');
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SCHED] scheduledTransactions table not found');
        return;
      }
      throw err;
    }
  },

  /**
   * Remove duplicate scheduled transactions with same detectionKey.
   * RECURRING-FIX: Issue 4B - Cleanup for existing duplicates.
   *
   * Policy (per GPT-5.2 recommendation):
   * - Prefer non-deleted over deleted
   * - If both non-deleted or both deleted, keep most recently updated
   *
   * Call this once on app startup to clean up legacy duplicates.
   * @returns Number of duplicates removed
   */
  async dedupeByDetectionKey(): Promise<number> {
    try {
      if (!db.scheduledTransactions) {
        return 0;
      }

      const all = await db.scheduledTransactions.toArray();
      const keyMap = new Map<string, ScheduledTransaction[]>();

      // Group by normalized detectionKey
      for (const sched of all) {
        if (!sched.detectionKey) continue;
        const key = sched.detectionKey.toLowerCase().trim();
        const group = keyMap.get(key) || [];
        group.push(sched);
        keyMap.set(key, group);
      }

      const toDelete: string[] = [];

      // For each group with duplicates, keep best one
      for (const [key, group] of keyMap) {
        if (group.length <= 1) continue;

        // Sort: non-deleted first, then by updatedAt desc
        group.sort((a, b) => {
          // Prefer non-deleted
          if (a.deletedAt && !b.deletedAt) return 1;
          if (!a.deletedAt && b.deletedAt) return -1;
          // If both same deleted state, prefer most recently updated
          return b.updatedAt.getTime() - a.updatedAt.getTime();
        });

        // Keep first (best), delete rest
        for (let i = 1; i < group.length; i++) {
          toDelete.push(group[i].id);
          log('[DEDUPE] Removing duplicate:', group[i].description, 'key:', key);
        }
      }

      // Hard delete duplicates (they're duplicates, don't need to sync)
      for (const id of toDelete) {
        await db.scheduledTransactions.delete(id);
      }

      if (toDelete.length > 0) {
        console.log(`[SCHEDULED] Deduped ${toDelete.length} duplicate schedules`);
        dataEvents.emit('all');
      }

      return toDelete.length;
    } catch (err) {
      console.error('[SCHEDULED] Dedupe failed:', err);
      return 0;
    }
  },
};

// ============================================================
// Skipped Occurrence Operations
// ============================================================

export const skippedOccurrenceService = {
  /**
   * Create a skipped occurrence record.
   */
  async create(
    scheduledTransactionId: string,
    skippedDate: Date,
    reason?: string
  ): Promise<string> {
    try {
      if (!db.skippedOccurrences) {
        console.warn('[SKIPPED] skippedOccurrences table not available');
        return '';
      }

      const id = generateId();
      const now = new Date();

      const record: SkippedOccurrence = {
        id,
        scheduledTransactionId,
        skippedDate: startOfDay(skippedDate),
        reason,
        createdAt: now,
      };

      await db.skippedOccurrences.add(record);
      log('[OK] Created skipped occurrence:', id, 'for date:', skippedDate);

      dataEvents.emit('all'); // BUG-034 FIX: Notify UI
      syncManager.triggerPush(); // BUG-034 FIX: Sync to other devices
      return id;
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SKIPPED] skippedOccurrences table not found');
        return '';
      }
      throw err;
    }
  },

  /**
   * Check if a date was skipped for a schedule.
   */
  async wasSkipped(scheduledTransactionId: string, date: Date): Promise<boolean> {
    try {
      if (!db.skippedOccurrences) {
        return false;
      }

      const targetDate = startOfDay(date);
      const record = await db.skippedOccurrences
        .where('[scheduledTransactionId+skippedDate]')
        .equals([scheduledTransactionId, targetDate])
        .first();
      return !!record;
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SKIPPED] skippedOccurrences table not found');
        return false;
      }
      throw err;
    }
  },

  /**
   * Get all skipped occurrences for a schedule.
   */
  async getBySchedule(scheduledTransactionId: string): Promise<SkippedOccurrence[]> {
    try {
      if (!db.skippedOccurrences) {
        return [];
      }
      return db.skippedOccurrences
        .where('scheduledTransactionId')
        .equals(scheduledTransactionId)
        .toArray();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SKIPPED] skippedOccurrences table not found');
        return [];
      }
      throw err;
    }
  },

  /**
   * Get skipped occurrences in a date range.
   */
  async getByDateRange(
    scheduledTransactionId: string,
    startDate: Date,
    endDate: Date
  ): Promise<SkippedOccurrence[]> {
    const all = await this.getBySchedule(scheduledTransactionId);
    return all.filter((s) => s.skippedDate >= startDate && s.skippedDate <= endDate);
  },

  /**
   * Delete a skipped occurrence.
   */
  async delete(id: string): Promise<void> {
    try {
      if (!db.skippedOccurrences) {
        console.warn('[SKIPPED] skippedOccurrences table not available');
        return;
      }
      await db.skippedOccurrences.delete(id);
      log('[OK] Deleted skipped occurrence:', id);

      dataEvents.emit('all'); // BUG-034 FIX: Notify UI
      syncManager.triggerPush(); // BUG-034 FIX: Sync to other devices
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[SKIPPED] skippedOccurrences table not found');
        return;
      }
      throw err;
    }
  },
};
